import { create } from 'zustand'

import type { ReportConfigOptions } from './reportConfig'

interface AnalysisWizardStoreActions {
  seedSectionsForCase: (
    caseType: string | null | undefined,
    config: ReportConfigOptions,
  ) => void
  addSection: (sectionKey: string, atIndex?: number) => void
  removeSection: (sectionKey: string) => void
  reorderSections: (sectionKeys: string[]) => void
  setSections: (sections: string[]) => void
  setSectionsInstructions: (sections: Record<string, string>) => void
  setSectionInstruction: (sectionKey: string, value: string) => void
  setInstructions: (value: string) => void
  reset: () => void
}

export interface AnalysisWizardStore {
  selectedSections: string[]
  sectionInstructions: Record<string, string>
  instructions: string

  actions: AnalysisWizardStoreActions
}

export const useAnalysisWizardStore = create<AnalysisWizardStore>(
  (set, get, store) => ({
    selectedSections: [],
    sectionInstructions: {},
    instructions: '',

    actions: {
      seedSectionsForCase: (caseType, config) => {
        const baseSections = caseType
          ? (config.suggestedSections[caseType] ?? config.allSectionsKeys)
          : config.allSectionsKeys

        const withoutPinned = baseSections.filter(
          (key) => !config.pinnedSectionsKeys.includes(key),
        )

        set({
          selectedSections: [
            ...config.pinnedTopSectionsKeys,
            ...withoutPinned,
            ...config.pinnedBottomSectionsKeys,
          ],
          sectionInstructions: {},
        })
      },

      addSection: (sectionKey, atIndex) => {
        const { selectedSections } = get()
        if (selectedSections.includes(sectionKey)) return
        if (atIndex !== undefined) {
          const next = [...selectedSections]
          next.splice(atIndex, 0, sectionKey)
          set({ selectedSections: next })
        } else {
          set({ selectedSections: [...selectedSections, sectionKey] })
        }
      },

      setSectionsInstructions: (sections) =>
        set({ sectionInstructions: sections }),

      setSections: (sections) => set({ selectedSections: sections }),

      removeSection: (sectionKey) =>
        set({
          selectedSections: get().selectedSections.filter(
            (key) => key !== sectionKey,
          ),
        }),

      reorderSections: (sectionKeys) => set({ selectedSections: sectionKeys }),

      setSectionInstruction: (sectionKey, value) =>
        set({
          sectionInstructions: {
            ...get().sectionInstructions,
            [sectionKey]: value,
          },
        }),

      setInstructions: (value) => set({ instructions: value }),

      reset: () => set(store.getInitialState()),
    },
  }),
)
